import { Injectable, Signal, signal } from '@angular/core';
import { Observable, of } from 'rxjs';
import {
  Destination,
  DestinationDetails,
  DestinationDisconnectResult,
  DestinationPlatform,
  DestinationPlatformWithDestinations,
  DestinationSettingsLoadResult,
  TikTokCreatorInfo,
  UpdateDestinationRequest,
  isFacebookPlatform,
  isYouTubeCatalogPlatform,
  YouTubeDestinationPlatformData,
  YouTubeVideoCategoryCatalog,
  YouTubeVideoCategoryItem,
} from '../../../domain/destinations';
import { HttpService } from '../../http/http';
import { Logger } from '../../logger';
import { environment } from 'app/environments/environment';

/** API row for `GET destinations/platforms` (Newtonsoft `JsonProperty` names). */
interface DestinationPlatformApiDto {
  platform_id: number;
  name: string;
  icon_asset_key?: string | null;
}

/** API row for `GET destinations` list. */
interface DestinationListApiDto {
  uid: string;
  platform_id: number;
  display_name: string;
  handle: string | null;
  account_image_url: string | null;
  status: string;
}

/** Response of `GET auth/integrations/{platform}/authorize-url` (message body only). */
interface AuthorizeUrlApiDto {
  url: string;
}

/** Response of `POST auth/integrations/{platformId}/{destinationUid}/disconnect` (message body only). */
interface DestinationDisconnectApiDto {
  disconnected: boolean;
  token_revoked: boolean;
  message?: string | null;
}

interface DestinationDetailsApiDto {
  uid: string;
  platform_id: number;
  display_name: string;
  handle: string | null;
  account_image_url: string | null;
  status: string;
  platform_data: Record<string, unknown> | null;
}

interface YouTubeVideoCategoryCatalogApiDto {
  items: { id: string; title: string }[] | null;
  is_default_catalog: boolean;
  message_code?: string | null;
}

interface TikTokCreatorInfoApiDto {
  creator_avatar_url?: string | null;
  creator_username?: string | null;
  creator_nickname?: string | null;
  privacy_level_options?: string[] | null;
  comment_disabled?: boolean | null;
  duet_disabled?: boolean | null;
  stitch_disabled?: boolean | null;
  max_video_post_duration_sec?: number | null;
}

function stablePositiveIntFromUid(uid: string): number {
  let h = 0;
  for (let i = 0; i < uid.length; i++) {
    h = (Math.imul(31, h) + uid.charCodeAt(i)) | 0;
  }
  const n = Math.abs(h);
  return n === 0 ? 1 : n;
}

function mapDestinationListRow(row: DestinationListApiDto): Destination {
  const uid = typeof row.uid === 'string' ? row.uid : String(row.uid);
  return {
    id: stablePositiveIntFromUid(uid),
    uid,
    tenantId: 0,
    userId: null,
    platform: row.platform_id,
    externalAccountId: '',
    displayName: row.display_name ?? '',
    handle: row.handle ?? null,
    accountImageUrl: row.account_image_url ?? null,
    status: row.status as Destination['status'],
    connectedAt: '',
    updatedAt: '',
  };
}

/**
 * Loads the global platform catalog and tenant destination rows from the API.
 * The backend is the source of truth for platform ids and names.
 */
@Injectable({ providedIn: 'root' })
export class DestinationsRepository {
  private readonly platformsCatalogInternal = signal<readonly DestinationPlatform[]>([]);
  private readonly tenantPlatformsWithDestinationsInternal = signal<
    readonly DestinationPlatformWithDestinations[]
  >([]);

  readonly platformsCatalog: Signal<readonly DestinationPlatform[]> =
    this.platformsCatalogInternal.asReadonly();

  readonly tenantPlatformsWithDestinations: Signal<
    readonly DestinationPlatformWithDestinations[]
  > = this.tenantPlatformsWithDestinationsInternal.asReadonly();

  constructor(
    private httpService: HttpService,
    private logger: Logger,
  ) {}

  /**
   * Loads platforms catalog, then tenant destinations, and merges into {@link tenantPlatformsWithDestinations}.
   */
  async init(): Promise<void> {
    await this.refreshPlatformsCatalog();
    await this.refreshTenantPlatformsWithDestinations();
  }

  /**
   * Current cached platforms catalog (same reference as {@link platformsCatalog} signal value).
   */
  getCachedPlatformsSnapshot(): readonly DestinationPlatform[] {
    return this.platformsCatalog();
  }

  /**
   * Last merged tenant view: one row per catalog platform, with `destinations` from the API (possibly empty).
   */
  getCachedTenantPlatformsWithDestinations(): readonly DestinationPlatformWithDestinations[] {
    return this.tenantPlatformsWithDestinations();
  }

  /**
   * Observable snapshot of {@link tenantPlatformsWithDestinations} (new array reference each subscription).
   */
  getPlatforms(): Observable<DestinationPlatformWithDestinations[]> {
    return of([...this.tenantPlatformsWithDestinations()]);
  }

  /**
   * Starts a platform OAuth connect flow. Returns the provider consent URL the caller must
   * redirect the browser to. Backend: `GET auth/integrations/{platform}/authorize-url`.
   */
  /**
   * Loads destination details for the settings panel (`GET api/destinations/{destinationUid}`).
   */
  async getDestination(destinationUid: string): Promise<DestinationDetails> {
    try {
      const options = this.httpService.createDefaultRequestOptions();
      const raw = (await this.httpService.get(
        `destinations/${destinationUid}`,
        options,
      )) as DestinationDetailsApiDto | null | undefined;
      if (!raw) {
        throw new Error('Destination details response was empty.');
      }
      return mapDestinationDetails(raw);
    } catch (err) {
      this.logger.error(
        `DestinationsRepository.getDestination(uid=${destinationUid}): ${err}`,
      );
      throw err;
    }
  }

  /**
   * Loads the YouTube video category catalog (`GET api/destinations/{platformId}/{uid}/video-categories`).
   */
  async getDestinationVideoCategories(
    platformId: number,
    destinationUid: string,
  ): Promise<YouTubeVideoCategoryCatalog> {
    try {
      const options = this.httpService.createDefaultRequestOptions();
      const raw = (await this.httpService.get(
        `destinations/${platformId}/${destinationUid}/video-categories`,
        options,
      )) as YouTubeVideoCategoryCatalogApiDto | null | undefined;
      return mapVideoCategoryCatalog(raw);
    } catch (err) {
      this.logger.error(
        `DestinationsRepository.getDestinationVideoCategories(platformId=${platformId}, uid=${destinationUid}): ${err}`,
      );
      throw err;
    }
  }

  /**
   * Loads live TikTok creator-info through the backend (`GET api/destinations/{platformId}/{uid}/creator-info`).
   */
  async getDestinationCreatorInfo(
    platformId: number,
    destinationUid: string,
  ): Promise<TikTokCreatorInfo> {
    if (!Number.isInteger(platformId) || platformId <= 0) {
      throw new Error(`Invalid platformId: ${platformId}`);
    }
    if (!destinationUid?.trim()) {
      throw new Error('destinationUid is required to load creator info.');
    }
    try {
      const options = this.httpService.createDefaultRequestOptions();
      const raw = (await this.httpService.get(
        `destinations/${platformId}/${encodeURIComponent(destinationUid.trim())}/creator-info`,
        options,
      )) as TikTokCreatorInfoApiDto | null | undefined;
      return mapTikTokCreatorInfo(raw);
    } catch (err) {
      this.logger.error(
        `DestinationsRepository.getDestinationCreatorInfo(platformId=${platformId}, uid=${destinationUid}): ${err}`,
      );
      throw err;
    }
  }

  /**
   * Loads data for the destination settings panel. YouTube catalog platforms fetch details and
   * video categories in parallel; other platforms fetch details only.
   */
  /**
   * Persists editable platform defaults (`PUT api/destinations/{destinationUid}`).
   */
  async updateDestinationSettings(
    destinationUid: string,
    request: UpdateDestinationRequest,
  ): Promise<void> {
    if (!destinationUid?.trim()) {
      throw new Error('destinationUid is required to update settings.');
    }
    try {
      const options = this.httpService.createDefaultRequestOptions();
      await this.httpService.put(
        `destinations/${encodeURIComponent(destinationUid.trim())}`,
        request,
        options,
      );
    } catch (err) {
      this.logger.error(
        `DestinationsRepository.updateDestinationSettings(uid=${destinationUid}): ${err}`,
      );
      throw err;
    }
  }

  async loadDestinationSettings(
    platform: DestinationPlatform,
    destinationUid: string,
  ): Promise<DestinationSettingsLoadResult> {
    if (isYouTubeCatalogPlatform(platform)) {
      const [details, videoCategories] = await Promise.all([
        this.getDestination(destinationUid),
        this.getDestinationVideoCategories(platform.platformId, destinationUid),
      ]);
      return { details, videoCategories };
    }
    const details = await this.getDestination(destinationUid);
    return { details, videoCategories: null };
  }

  /**
   * Disconnects a destination (`POST auth/integrations/{platformId}/{destinationUid}/disconnect`).
   */
  async disconnectDestination(
    platformId: number,
    destinationUid: string,
  ): Promise<DestinationDisconnectResult> {
    if (!Number.isInteger(platformId) || platformId <= 0) {
      throw new Error(`Invalid platformId: ${platformId}`);
    }
    if (!destinationUid?.trim()) {
      throw new Error('destinationUid is required to disconnect.');
    }
    try {
      const options = this.httpService.createDefaultRequestOptions();
      const absoluteUrl = this.buildAuthIntegrationsUrl(
        `${platformId}/${encodeURIComponent(destinationUid.trim())}/disconnect`,
      );
      const raw = (await this.httpService.post(absoluteUrl, {}, options)) as
        | DestinationDisconnectApiDto
        | null
        | undefined;
      if (!raw || typeof raw.disconnected !== 'boolean') {
        throw new Error('disconnect response was invalid.');
      }
      return {
        disconnected: raw.disconnected,
        token_revoked: !!raw.token_revoked,
        message: raw.message ?? null,
      };
    } catch (err) {
      this.logger.error(
        `DestinationsRepository.disconnectDestination(platformId=${platformId}, uid=${destinationUid}): ${err}`,
      );
      throw err;
    }
  }

  async getAuthorizeUrl(platformId: number, returnUrl: string): Promise<string> {
    if (!Number.isInteger(platformId) || platformId <= 0) {
      throw new Error(`Invalid platformId: ${platformId}`);
    }
    if (!returnUrl) {
      throw new Error('returnUrl is required to start the OAuth flow.');
    }
    try {
      const options = this.httpService.createDefaultRequestOptions();
      const encodedReturnUrl = encodeURIComponent(returnUrl);
      const absoluteUrl = `${this.buildAuthIntegrationsUrl(`${platformId}/authorize-url`)}?returnUrl=${encodedReturnUrl}`;
      const response = (await this.httpService.get(
        absoluteUrl,
        options,
      )) as AuthorizeUrlApiDto | null | undefined;
      const url = response?.url?.trim();
      if (!url) {
        throw new Error('authorize-url response did not contain a url.');
      }
      return url;
    } catch (err) {
      this.logger.error(
        `DestinationsRepository.getAuthorizeUrl(platformId=${platformId}): ${err}`,
      );
      throw err;
    }
  }

  async refreshTenantPlatformsWithDestinations(): Promise<void> {
    const catalog = [...this.platformsCatalogInternal()];
    if (catalog.length === 0) {
      this.tenantPlatformsWithDestinationsInternal.set(
        Object.freeze([]) as readonly DestinationPlatformWithDestinations[],
      );
      return;
    }

    try {
      const options = this.httpService.createDefaultRequestOptions();
      const raw = (await this.httpService.get('destinations', options)) as
        | DestinationListApiDto[]
        | null
        | undefined;
      const list = Array.isArray(raw) ? raw : [];
      const destinations = list.map((row) => mapDestinationListRow(row));
      const byPlatform = new Map<number, Destination[]>();
      for (const d of destinations) {
        const arr = byPlatform.get(d.platform) ?? [];
        arr.push(d);
        byPlatform.set(d.platform, arr);
      }
      const merged: DestinationPlatformWithDestinations[] = catalog.map((p) => ({
        platform: {
          platformId: p.platformId,
          name: p.name,
          iconUrl: p.iconUrl,
        },
        destinations: byPlatform.get(p.platformId) ?? [],
      }));
      this.tenantPlatformsWithDestinationsInternal.set(
        Object.freeze(merged) as readonly DestinationPlatformWithDestinations[],
      );
    } catch (err) {
      this.logger.error(
        `DestinationsRepository.refreshTenantPlatformsWithDestinations: ${err}`,
      );
      const emptyDestinations = catalog.map((p) => ({
        platform: {
          platformId: p.platformId,
          name: p.name,
          iconUrl: p.iconUrl,
        },
        destinations: [] as Destination[],
      }));
      this.tenantPlatformsWithDestinationsInternal.set(
        Object.freeze(emptyDestinations) as readonly DestinationPlatformWithDestinations[],
      );
    }
  }

  private async refreshPlatformsCatalog(): Promise<void> {
    try {
      const options = this.httpService.createDefaultRequestOptions();
      const items = (await this.httpService.get(
        'destinations/platforms',
        options,
      )) as DestinationPlatformApiDto[] | null | undefined;

      const mapped =
        Array.isArray(items) && items.length > 0
          ? items.map((row) => this.mapApiPlatform(row))
          : [];

      this.platformsCatalogInternal.set(Object.freeze(mapped) as readonly DestinationPlatform[]);
    } catch (err) {
      this.logger.error(`DestinationsRepository.refreshPlatformsCatalog: ${err}`);
      this.platformsCatalogInternal.set(Object.freeze([]) as readonly DestinationPlatform[]);
    }
  }

  private mapApiPlatform(row: DestinationPlatformApiDto): DestinationPlatform {
    const raw = row.icon_asset_key;
    const iconUrl =
      typeof raw === 'string' && raw.trim().length > 0 ? raw.trim() : null;
    return {
      platformId: row.platform_id,
      name: row.name ?? '',
      iconUrl,
    };
  }

  private getFacebookPlatformId(): number {
    const platform = this.platformsCatalogInternal().find(isFacebookPlatform);
    if (!platform) {
      throw new Error('Facebook destination platform is not available.');
    }
    return platform.platformId;
  }

  /** `auth/integrations/...` lives at the site root (not under `/api/`). */
  private buildAuthIntegrationsUrl(path: string): string {
    const baseOrigin = (
      environment.ENDPOINT && environment.ENDPOINT.trim().length > 0
        ? environment.ENDPOINT
        : window.location.origin
    ).replace(/\/+$/, '');
    const normalizedPath = path.replace(/^\/+/, '');
    return `${baseOrigin}/auth/integrations/${normalizedPath}`;
  }
}

function mapDestinationDetails(row: DestinationDetailsApiDto): DestinationDetails {
  return {
    uid: typeof row.uid === 'string' ? row.uid : String(row.uid),
    platformId: row.platform_id,
    displayName: row.display_name ?? '',
    handle: row.handle ?? null,
    accountImageUrl: row.account_image_url ?? null,
    status: row.status ?? '',
    platformData: mapPlatformData(row.platform_id, row.platform_data),
  };
}

function mapPlatformData(
  platformId: number,
  raw: Record<string, unknown> | null,
): DestinationDetails['platformData'] {
  if (!raw) {
    return null;
  }
  switch (platformId) {
    case 1:
      return {
        privacy: stringOrNull(raw['privacy']),
        self_declared_made_for_kids: boolOrNull(raw['self_declared_made_for_kids']),
        notify_subscribers: boolOrNull(raw['notify_subscribers']),
        category_id: stringOrNull(raw['category_id']),
      } as YouTubeDestinationPlatformData;
    case 2:
      return {
        privacy_level: stringOrNull(raw['privacy_level']),
        disable_comment: boolOrNull(raw['disable_comment']),
        disable_duet: boolOrNull(raw['disable_duet']),
        disable_stitch: boolOrNull(raw['disable_stitch']),
        publish_mode: stringOrNull(raw['publish_mode']),
        content_disclosure_enabled: boolOrNull(raw['content_disclosure_enabled']),
        is_brand_content: boolOrNull(raw['is_brand_content']),
        is_brand_organic: boolOrNull(raw['is_brand_organic']),
        is_aigc: boolOrNull(raw['is_aigc']),
        max_video_post_duration_sec: numberOrNull(raw['max_video_post_duration_sec']),
      };
    case 3:
      return {
        default_post_type: stringOrNull(raw['default_post_type']),
        share_to_feed: boolOrNull(raw['share_to_feed']),
        is_ai_generated: boolOrNull(raw['is_ai_generated']),
      };
    case 4:
      return {
        default_publish_target: stringOrNull(raw['default_publish_target']),
        reels_eligible: boolOrNull(raw['reels_eligible']),
        embeddable: boolOrNull(raw['embeddable']),
        no_story: boolOrNull(raw['no_story']),
        content_category: stringOrNull(raw['content_category']),
      };
    default:
      return null;
  }
}

function mapTikTokCreatorInfo(
  raw: TikTokCreatorInfoApiDto | null | undefined,
): TikTokCreatorInfo {
  const privacyLevelOptions = Array.isArray(raw?.privacy_level_options)
    ? raw!.privacy_level_options.filter((x): x is string => typeof x === 'string')
    : [];
  return {
    creatorAvatarUrl: stringOrNull(raw?.creator_avatar_url),
    creatorUsername: stringOrNull(raw?.creator_username),
    creatorNickname: stringOrNull(raw?.creator_nickname),
    privacyLevelOptions: Object.freeze([...privacyLevelOptions]) as readonly string[],
    commentDisabled: raw?.comment_disabled === true,
    duetDisabled: raw?.duet_disabled === true,
    stitchDisabled: raw?.stitch_disabled === true,
    maxVideoPostDurationSec: numberOrNull(raw?.max_video_post_duration_sec) ?? 0,
  };
}

function mapVideoCategoryCatalog(
  raw: YouTubeVideoCategoryCatalogApiDto | null | undefined,
): YouTubeVideoCategoryCatalog {
  const items = Array.isArray(raw?.items) ? raw.items : [];
  return {
    items: Object.freeze(
      items.map(
        (row): YouTubeVideoCategoryItem => ({
          id: row.id ?? '',
          title: row.title ?? '',
        }),
      ),
    ) as readonly YouTubeVideoCategoryItem[],
    isDefaultCatalog: !!raw?.is_default_catalog,
    messageCode: raw?.message_code ?? null,
  };
}

function stringOrNull(value: unknown): string | null {
  return typeof value === 'string' ? value : value == null ? null : String(value);
}

function boolOrNull(value: unknown): boolean | null {
  return typeof value === 'boolean' ? value : null;
}

function numberOrNull(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}
